import { BadgeInfoComponent } from '@components/shared/badge-info/badge-info.component';
import { LoginTokenComponent } from '@components/shared/login-token/login-token.component';
import { BadgeInfo } from '@models/badge.model';
import { TranslateModule } from '@ngx-translate/core';
import { IBookingStatusModel } from '@services/api/booking-status/booking-status-model.interface';
import { IBookingModel } from '@services/api/booking/booking-model.interface';
import { BookingStatusService } from '@services/util/booking-status.service';
import { LogService } from '@services/util/log.service';
import { SelectItemLoaderService, UiSelectItem } from '@services/util/select-item-loader.service';
import { DatePickerModule } from 'primeng/datepicker';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
// PrimeNG imports
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BookingActionEvent } from '../../models/booking-detail.model';
import { BookingChangeTrackerService } from '../../services/booking-change-tracker.service';

@Component({
  selector: 'app-booking-general-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    SelectModule,
    DropdownModule,
    DatePickerModule,
    InputNumberModule,
    InputTextModule,
    TextareaModule,
    BadgeInfoComponent,
    LoginTokenComponent
  ],
  templateUrl: './booking-general-tab.component.html'
})
export class BookingGeneralTabComponent implements OnInit, OnChanges {
  @Input() booking: IBookingModel | null = null;
  @Output() actionEvent = new EventEmitter<BookingActionEvent>();

  loading = false;
  bookingStatusItems: UiSelectItem<IBookingStatusModel>[] = [];
  selectedStatusId: string | null = null;

  /**
   * Map of booking status badges for UI display
   */
  private badgeMap: Record<number, BadgeInfo> = {};

  readonly changeTracker = inject(BookingChangeTrackerService);
  private readonly selectItemLoader = inject(SelectItemLoaderService);
  private readonly bookingStatusService = inject(BookingStatusService);
  private readonly logService = inject(LogService);

  ngOnInit(): void {
    // Load options first
    this.loadBookingStatusOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle when booking input changes
    if (changes['booking'] && this.booking?.bookingStatus?.['@id']) {
      this.selectedStatusId = this.booking.bookingStatus['@id'];
      this.logService.debug('selectedStatusId updated from changes:', this.selectedStatusId);
    }
  }

  /**
   * Calculates the total number of occupants (adults + children)
   * @returns The total number of occupants
   */
  getTotalOccupants(): number {
    if (!this.booking) return 0;
    const adults = this.booking.adult_occupants || 0;
    const children = this.booking.child_occupants || 0;
    return adults + children;
  }

  /**
   * Handler for field changes for a specific field
   * @param fieldName Name of the field that changed
   * @param newValue New value of the field
   */
  onFieldChange(fieldName: string, newValue: any): void {
    // Emit field change to parent component
    if (this.booking) {
      this.actionEvent.emit({
        action: 'field-change',
        booking: this.booking,
        additionalData: {
          fieldName,
          newValue
        }
      });
    }
  }

  /**
   * Handler for status change events
   */
  onStatusChange(): void {
    if (this.booking && this.selectedStatusId) {
      // Find the selected status model from the items list using the IRI
      const selectedStatus = this.bookingStatusItems.find((item) => item.value === this.selectedStatusId)?.model;

      // Update the booking status with the found model
      if (selectedStatus) {
        this.booking.bookingStatus = selectedStatus;
        this.onFieldChange('bookingStatus', this.booking.bookingStatus);
      }
    }
  }

  /**
   * Get the badge for the currently selected status
   * @returns The badge info for the selected status, or undefined if not available
   */
  getSelectedStatusBadge(): BadgeInfo | undefined {
    if (this.booking?.bookingStatus?.id) {
      return this.getStatusBadge(this.booking.bookingStatus.id);
    }
    return undefined;
  }

  /**
   * Get a booking status badge by ID
   * @param statusId Status ID
   * @returns BadgeInfo instance or undefined if not found
   */
  getStatusBadge(statusId: number | undefined): BadgeInfo | undefined {
    if (!statusId) return undefined;
    return this.badgeMap[statusId];
  }

  /**
   * Gets the localized title of the currently selected booking status
   * @returns The localized title of the selected booking status
   */
  getSelectedStatusLabel(): string {
    if (!this.booking?.bookingStatus?.id) return '';
    const statusId = this.booking.bookingStatus.id;
    const badgeInfo = this.getStatusBadge(statusId);
    return badgeInfo?.text || '';
  }

  /**
   * Loads the booking status dropdown items
   */
  private loadBookingStatusOptions(): void {
    this.loading = true;

    this.selectItemLoader.getBookingStatusSelectItems().subscribe({
      next: (items: UiSelectItem<IBookingStatusModel>[]) => {
        this.bookingStatusItems = items;

        // Create badge for each booking status
        items.forEach((item: UiSelectItem<IBookingStatusModel>) => {
          if (item.model && item.model.id) {
            const status = item.model;
            this.badgeMap[status.id] = new BadgeInfo(
              status.id,
              item.localizableTitle || '',
              this.bookingStatusService.getStatusColorClasses(status),
              this.bookingStatusService.getStatusIconClasses(status)
            );
          }
        });

        // Update selectedStatusId after options are loaded
        if (this.booking?.bookingStatus?.['@id']) {
          this.selectedStatusId = this.booking.bookingStatus['@id'];
        }

        this.loading = false;
      },
      error: (err) => {
        this.logService.error('Error loading booking status options:', err);
        this.loading = false;
      }
    });
  }
}
